/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { LiteraMessage } from "../../lib/litera-api";
import { tryActiveLiteraStorageKeys } from "../../lib/litera-storage";

export type LiteraGeneratorStatus = "idle" | "generating" | "completed" | "error";

export type LiteraGenerationPayload = {
  projectId: string;
  phase: string;
  hiddenPhase?: string | null;
  hiddenSystemPrompt?: string | null;
  visibleSystemPrompt: string;
  messages: LiteraMessage[];
  bible: unknown;
  projectMemory?: unknown;
  /** Phase 3 per-chapter context (active chapter + prev excerpt). */
  perChapter?: unknown;
  /** S3 read-context: chapters the user selected for LITRA to read (ephemeral). */
  readChapters?: unknown;
  maxTokens?: number;
  hiddenMaxTokens?: number;
};

export type LiteraGenerationJob = LiteraGenerationPayload & {
  jobId: string;
  startedAt: number;
};

type GeneratorSnapshot = {
  status: LiteraGeneratorStatus;
  progress: number;
  streamingText: string;
  completedText: string;
  hiddenAnalysis: string;
  error: string | null;
  activeJob: LiteraGenerationJob | null;
  lastCompletedJob: LiteraGenerationJob | null;
  notification: string | null;
};

type GeneratorContextValue = GeneratorSnapshot & {
  startGeneration: (payload: LiteraGenerationPayload) => string;
  stopGeneration: () => void;
  resetGeneration: () => void;
  clearNotification: () => void;
};

const STORAGE_KEY_FALLBACK = "bbg-litera-generator-state";
const GeneratorContext = createContext<GeneratorContextValue | null>(null);

function generatorStorageKey(): string {
  return tryActiveLiteraStorageKeys()?.generatorState || STORAGE_KEY_FALLBACK;
}

const initialState: GeneratorSnapshot = {
  status: "idle",
  progress: 0,
  streamingText: "",
  completedText: "",
  hiddenAnalysis: "",
  error: null,
  activeJob: null,
  lastCompletedJob: null,
  notification: null,
};

function persist(state: GeneratorSnapshot) {
  try {
    localStorage.setItem(generatorStorageKey(), JSON.stringify({
      status: state.status,
      progress: state.progress,
      streamingText: state.streamingText,
      completedText: state.completedText,
      hiddenAnalysis: state.hiddenAnalysis,
      error: state.error,
      activeJob: state.activeJob,
      lastCompletedJob: state.lastCompletedJob,
      notification: state.notification,
    }));
  } catch {}
}

function restore(): GeneratorSnapshot {
  try {
    const raw = localStorage.getItem(generatorStorageKey());
    if (!raw) return initialState;
    const parsed = JSON.parse(raw);
    return {
      ...initialState,
      ...parsed,
      status: parsed.status === "generating" ? "idle" : parsed.status,
      activeJob: parsed.status === "generating" ? null : parsed.activeJob,
    };
  } catch {
    return initialState;
  }
}

export function LiteraGeneratorProvider({ children }: { children: ReactNode }) {
  const workerRef = useRef<Worker | null>(null);
  const [state, setState] = useState<GeneratorSnapshot>(() => restore());

  useEffect(() => {
    persist(state);
  }, [state]);

  const ensureWorker = useCallback(() => {
    if (!workerRef.current) {
      workerRef.current = new Worker(new URL("./literaWorker.ts", import.meta.url), { type: "module" });
      workerRef.current.onmessage = event => {
        const message = event.data as {
          type: string;
          chunk?: string;
          fullText?: string;
          hiddenAnalysis?: string;
          progress?: number;
          error?: string;
        };
        setState(current => {
          if (message.type === "progress") {
            return { ...current, progress: message.progress ?? current.progress };
          }
          if (message.type === "hiddenDone") {
            return {
              ...current,
              hiddenAnalysis: message.hiddenAnalysis || "",
              progress: message.progress ?? 35,
            };
          }
          if (message.type === "chunk") {
            return {
              ...current,
              streamingText: `${current.streamingText}${message.chunk || ""}`,
              progress: message.progress ?? current.progress,
            };
          }
          if (message.type === "done") {
            return {
              ...current,
              status: "completed",
              progress: 100,
              completedText: message.fullText || current.streamingText,
              hiddenAnalysis: message.hiddenAnalysis || current.hiddenAnalysis,
              streamingText: "",
              error: null,
              lastCompletedJob: current.activeJob,
              notification: "LITRA er færdig med det aktuelle kapitel",
            };
          }
          if (message.type === "error") {
            return {
              ...current,
              status: "error",
              error: message.error || "LITRA generation failed.",
              streamingText: "",
            };
          }
          if (message.type === "stopped") {
            return {
              ...current,
              status: "idle",
              progress: 0,
              streamingText: "",
              error: null,
              activeJob: null,
            };
          }
          return current;
        });
      };
    }
    return workerRef.current;
  }, []);

  const startGeneration = useCallback((payload: LiteraGenerationPayload) => {
    const job: LiteraGenerationJob = {
      ...payload,
      jobId: `litera-job-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      startedAt: Date.now(),
    };
    setState({
      status: "generating",
      progress: 1,
      streamingText: "",
      completedText: "",
      hiddenAnalysis: "",
      error: null,
      activeJob: job,
      lastCompletedJob: null,
      notification: null,
    });

    void (async () => {
      try {
        const { getFirebaseIdToken } = await import("../../lib/anthropic-proxy-client");
        const firebaseIdToken = await getFirebaseIdToken();
        ensureWorker().postMessage({
          type: "start",
          firebaseIdToken,
          payload: {
            ...job,
            outline: [],
          },
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Log ind for at bruge LITRA.";
        setState(current => ({
          ...current,
          status: "error",
          progress: 0,
          error: message,
          activeJob: null,
          notification: null,
        }));
      }
    })();

    return job.jobId;
  }, [ensureWorker]);

  const terminateWorker = useCallback(() => {
    workerRef.current?.postMessage({ type: "stop" });
    workerRef.current?.terminate();
    workerRef.current = null;
  }, []);

  const stopGeneration = useCallback(() => {
    terminateWorker();
    setState(current => ({
      ...current,
      status: "idle",
      progress: 0,
      streamingText: "",
      error: null,
      activeJob: null,
      notification: "LITRA blev stoppet",
    }));
  }, [terminateWorker]);

  const resetGeneration = useCallback(() => {
    terminateWorker();
    setState(initialState);
  }, [terminateWorker]);

  const clearNotification = useCallback(() => {
    setState(current => ({
      ...current,
      status: current.status === "completed" || current.status === "error" ? "idle" : current.status,
      notification: null,
    }));
  }, []);

  const value = useMemo<GeneratorContextValue>(() => ({
    ...state,
    startGeneration,
    stopGeneration,
    resetGeneration,
    clearNotification,
  }), [clearNotification, resetGeneration, startGeneration, state, stopGeneration]);

  return <GeneratorContext.Provider value={value}>{children}</GeneratorContext.Provider>;
}

export function useLiteraGenerator() {
  const ctx = useContext(GeneratorContext);
  if (!ctx) throw new Error("useLiteraGenerator must be used inside LiteraGeneratorProvider");
  return ctx;
}
